<template>
    <div class="table-container">
        <table>
            <thead class="font-6">
                <tr>
                    <th v-for="column in columns"
                        :key="column.id"
                        @click="!column.noSort && sortChanged(column.id)"
                        :class="getColumnClass(column)">
                        <slot :columnCell="column" name="column-cell">
                            <span>{{column.text}}</span>
                        </slot>
                    </th>
                </tr>
            </thead>
            <tbody class="font-5">
                <tr v-for="(row, rowIndex) in sortedRowSlice" :key="rowIndex">
                    <td v-for="(_, columnIndex) in columns" :key="row[columnIndex].id">
                        <slot :row="row" :rowCell="row[columnIndex]" :columnIndex="columnIndex" name="row-cell">
                            <span :class="{ 'font-4': columnIndex === selectedColumnIndex }">
                                {{row[columnIndex].text}}
                            </span>
                        </slot>
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="footer" class="font-5">
                <tr>
                    <td v-for="footerCell in footer" :key="footerCell.id">
                        <slot :footerCell="footerCell" name="footer-cell">
                            <span>{{footerCell.text}}</span>
                        </slot>
                    </td>
                </tr>
            </tfoot>
        </table>
        <Pagination :page="page"
                    v-if="pagination"
                    :pageSize="tablePageSize"
                    :total="rows.length"
                    @pageSizeChanged="pageSizeChanged"
                    @pageChanged="pageChanged"></Pagination>
    </div>
</template>

<script>
import Pagination from 'components/table/Pagination.vue';

export default {
    name: 'Table',
    components: { Pagination },
    data() {
        return {
            page: 1,
            tablePageSize: this.pageSize
        };
    },
    props: {
        pageSize: {
            type: Number,
            default: 10
        },
        pagination: {
            type: Boolean,
            default: true
        },
        rows: {
            type: Array,
            required: true
        },
        columns: {
            type: Array,
            required: true
        },
        footer: {
            type: Array
        },
        sortBy: {
            type: String,
            required: true
        },
        sortDirection: {
            type: String,
            default: 'desc'
        }
    },
    watch: {
        pageSize(newPageSize) {
            this.tablePageSize = newPageSize;
        }
    },
    computed: {
        selectedColumnIndex() {
            return this.columns.findIndex((column) => column.id === this.sortBy);
        },
        sortedRows() {
            const { selectedColumnIndex } = this;

            // don't sort if 'sortBy' column not found
            if (selectedColumnIndex === -1) {
                return this.rows.slice();
            }

            return this.rows.slice().sort((a, b) => {
                const toReturn = this.sortDirection === 'asc' ? 1 : -1;
                const prev = a[selectedColumnIndex].value;
                const next = b[selectedColumnIndex].value;

                if (prev > next) {
                    return toReturn;
                }

                if (next > prev) {
                    return -toReturn;
                }

                return 0;
            });
        },
        sortedRowSlice() {
            const from = ((this.page - 1) * this.tablePageSize);
            const to = from + this.tablePageSize;

            return this.sortedRows.slice(from, to);
        }
    },
    methods: {
        getColumnClass(column) {
            if (column.noSort) {
                return 'column-no-sort';
            }

            const isSelected = column.id === this.sortBy;
            const sortAscending = this.sortDirection === 'asc';

            return {
                sort: isSelected,
                'sort-asc': isSelected && sortAscending,
                'sort-desc': isSelected && !sortAscending
            };
        },
        pageChanged(page) {
            this.page = page;
        },
        pageSizeChanged(pageSize) {
            this.tablePageSize = pageSize;
            this.$emit('pageSizeChanged', pageSize);
        },
        sortChanged(columnId) {
            let { sortDirection } = this;

            if (columnId === this.sortBy) {
                sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            }

            this.$emit('sortChanged', columnId, sortDirection);
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide";

/* [class*="column"] added to be more specific than just .column-no-sort which is overridden by angular (main.css) */
.column-no-sort[class*="column"] {
    float: unset;
}

table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;

    thead {
        border-bottom: 2px solid $primary-500;

        th {
            position: relative;
            cursor: pointer;
            text-align: left;
            padding: 10px;
            max-width: 110px;
            user-select: none;

            &:not(.column-no-sort):hover {
                color: $primary-500;
            }

            &.column-no-sort {
                cursor: default;
            }

            &.sort {
                color: $primary-500;
            }

            &.sort:before {
                position: absolute;
                left: 6px;
                z-index: 1;
                content: "";
                display: block;
                width: 0;
                height: 0;
                border: .4375rem inset;
                border-color: $primary-500 transparent transparent;
                border-top-style: solid;
            }

            &.sort-asc:before {
                bottom: -1px;
                border-color: transparent transparent $primary-500;
            }

            &.sort-desc:before {
                bottom: -.9375rem;
                border-color: $primary-500 transparent transparent;
            }
        }
    }

    tbody {
        tr {
            height: 55px;

            &:hover {
                background-color: $light-200;
            }

            &:nth-of-type(even) {
                background-color: $light-100;

                &:hover {
                    background-color: $light-200;
                }
            }

            &:first-child {
                td {
                    border-top: none;
                }
            }

            td {
                padding: 10px;
                border: $border;
                line-height: 17px;
            }
        }
    }

    tfoot {
        tr {
            height: 40px;
            background: $dark-300;

            td {
                padding: 5px 10px;
                color: $white;
            }
        }
    }
}
</style>
